import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@vaiu/ui/card";
import { Button } from "@vaiu/ui/button";
import { Badge } from "@vaiu/ui/badge";
import { Separator } from "@vaiu/ui/separator";
import { Link } from "react-router-dom";
import { ArrowLeft, Heart, Github, ExternalLink, Package } from "lucide-react";

interface ManualContributor {
  name: string;
  role: string;
  contribution: string;
  url?: string;
}

interface PackageContributor {
  name: string;
  version: string;
  description?: string;
  author?: string | { name: string; email?: string; url?: string };
  maintainers?: Array<{ name: string; email?: string }>;
  key_contributors?: string[];
  homepage?: string;
  repository?: string | { url: string };
  license?: string;
}

interface ContributorsData {
  manual_contributors: ManualContributor[];
  package_contributors: Record<string, PackageContributor>;
  last_updated: string;
  generated_by: string;
}

const AcknowledgementsPage: React.FC = () => {
  const [contributors, setContributors] = useState<ContributorsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchContributors = async () => {
      try {
        const response = await fetch('/contributors.json');
        if (!response.ok) {
          throw new Error('Failed to load contributors');
        }
        const data = await response.json();
        setContributors(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchContributors();
  }, []);

  const formatAuthor = (author: string | { name: string; email?: string; url?: string } | undefined) => {
    if (!author) return 'Unknown';
    if (typeof author === 'string') return author;
    return author.name;
  };

  const getAuthorUrl = (author: string | { name: string; email?: string; url?: string } | undefined) => {
    if (!author || typeof author === 'string') return null;
    return author.url;
  };

  const getRepositoryUrl = (repository: string | { url: string } | undefined) => {
    if (!repository) return null;
    if (typeof repository === 'string') return repository;
    return repository.url;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading contributors...</p>
        </div>
      </div>
    );
  }

  if (error || !contributors) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Error loading contributors: {error}</p>
          <Link to="/">
            <Button variant="outline" className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const packageContributorsList = Object.entries(contributors.package_contributors).sort(([a], [b]) => 
    a.toLowerCase().localeCompare(b.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                  <Heart className="w-8 h-8 text-red-500 mr-3" />
                  Acknowledgements
                </h1>
                <p className="text-gray-600 mt-1">
                  Recognizing the open-source contributors who make this app possible
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        


        {/* Open Source Dependencies Section */}
        {packageContributorsList.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Package className="w-5 h-5 mr-2" />
                Open Source Dependencies
              </CardTitle>
              <CardDescription>
                This application is built on the shoulders of giants. We acknowledge and thank the 
                maintainers and contributors of these open source packages ({packageContributorsList.length} total)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                {packageContributorsList.map(([packageName, packageInfo]) => (
                  <div key={packageName} className="p-3 bg-gray-50 rounded border">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h4 className="font-medium text-gray-900">{packageName}</h4>
                          <Badge variant="outline" className="text-xs">
                            v{packageInfo.version}
                          </Badge>
                          {packageInfo.license && (
                            <Badge variant="secondary" className="text-xs">
                              {packageInfo.license}
                            </Badge>
                          )}
                        </div>
                        
                        {packageInfo.description && (
                          <p className="text-sm text-gray-600 mt-1">
                            {packageInfo.description}
                          </p>
                        )}
                        
                        {/* Key Contributors */}
                        {packageInfo.key_contributors && packageInfo.key_contributors.length > 0 && (
                          <div className="mt-3 p-2 bg-blue-50 rounded border-l-4 border-blue-400">
                            <div className="flex items-center text-sm">
                              <span className="font-semibold text-blue-900">Key Contributors:</span>
                              <span className="ml-2 text-blue-800">
                                {packageInfo.key_contributors.slice(0, 4).join(', ')}
                                {packageInfo.key_contributors.length > 4 && ` + ${packageInfo.key_contributors.length - 4} more`}
                              </span>
                            </div>
                          </div>
                        )}

                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                          {packageInfo.author && (
                            <div className="flex items-center">
                              <span className="font-medium">Author:</span>
                              <span className="ml-1">
                                {getAuthorUrl(packageInfo.author) ? (
                                  <a 
                                    href={getAuthorUrl(packageInfo.author)!} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800"
                                  >
                                    {formatAuthor(packageInfo.author)}
                                  </a>
                                ) : (
                                  formatAuthor(packageInfo.author)
                                )}
                              </span>
                            </div>
                          )}
                          
                          {packageInfo.maintainers && packageInfo.maintainers.length > 0 && (
                            <div className="flex items-center">
                              <span className="font-medium">Maintainers:</span>
                              <span className="ml-1">
                                {packageInfo.maintainers.slice(0, 2).map(m => m.name).join(', ')}
                                {packageInfo.maintainers.length > 2 && ` + ${packageInfo.maintainers.length - 2} more`}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex space-x-1">
                        {packageInfo.homepage && (
                          <a 
                            href={packageInfo.homepage} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-gray-600"
                            title="Homepage"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                        
                        {getRepositoryUrl(packageInfo.repository) && (
                          <a 
                            href={getRepositoryUrl(packageInfo.repository)!} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-gray-600"
                            title="Repository"
                          >
                            <Github className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer Information */}
        <Card>
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="text-sm text-gray-600">
                <p className="flex items-center justify-center">
                  <Heart className="w-4 h-4 text-red-500 mr-1" />
                  Thank you to all open-source contributors who made this application possible
                </p>
              </div>
              
              <Separator />
              
              <div className="text-xs text-gray-500 space-y-2">
                <p>
                  <strong>Last Updated:</strong> {new Date(contributors.last_updated).toLocaleDateString()}
                </p>
                <p>
                  <strong>Generated by:</strong> {contributors.generated_by}
                </p>
                <p>
                  To update package contributors, run: <code className="bg-gray-100 px-1 rounded">npm run update-contributors</code>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AcknowledgementsPage; 